// Pause/play button for content that moves on its own (WCAG 2.2.2).
// Wraps the moving element ([data-motion-target]) and a .motion-toggle__button.
// The target is either a Swiper carousel (its autoplay is stopped and started)
// or an element with a CSS animation (it gets the motion-paused / motion-playing classes).
// Motion starts paused when the visitor prefers reduced motion.
if (!customElements.get('motion-toggle')) {
  customElements.define(
    'motion-toggle',
    class MotionToggle extends HTMLElement {
      constructor() {
        super();
        this.target = this.querySelector('[data-motion-target]');
        this.button = this.querySelector('.motion-toggle__button');
        this.button.addEventListener('click', () => this.setPaused(!this.paused));
      }

      connectedCallback() {
        // Carousels are created by other deferred scripts, which have all run by DOMContentLoaded.
        if (document.readyState === 'complete') return this.init();
        const init = () => this.init();
        document.addEventListener('DOMContentLoaded', init, { once: true });
        window.addEventListener('load', init, { once: true });
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;
        this.swiper = this.target.swiper;
        if (this.swiper) {
          // Swiper also stops autoplay by itself, e.g. when the arrows or dots are used.
          this.swiper.on('autoplayStart autoplayStop', () => this.updateButton());
          // Swiper 8 restarts a stopped autoplay when the tab is shown again if it was stopped mid-transition.
          this.swiper.on('autoplayStop', () => (this.swiper.autoplay.paused = false));
        }
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.setPaused(true);
        } else {
          this.updateButton();
        }
      }

      get paused() {
        if (this.swiper) return !this.swiper.autoplay.running;
        return this.target.classList.contains('motion-paused');
      }

      setPaused(paused) {
        if (this.swiper) {
          const autoplay = this.swiper.autoplay;
          if (paused) {
            autoplay.stop();
          } else if (!autoplay.start()) {
            // Swiper 8 can refuse to start again after a stop; run it directly.
            autoplay.running = true;
            autoplay.run();
          }
        } else {
          this.target.classList.toggle('motion-paused', paused);
          this.target.classList.toggle('motion-playing', !paused);
        }
        this.updateButton();
      }

      updateButton() {
        const paused = this.paused;
        this.button.classList.toggle('motion-toggle__button--paused', paused);
        this.button.setAttribute('aria-label', paused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel);
      }
    }
  );
}
